import {
  measureText,
  Stack,
  styled,
  Typography,
  useTypographyVariantFontForMeasuringText,
} from '@chronosphereio/chrono-ui';
import React, { useCallback, useMemo, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import type {
  BarChartData,
  BarClickEvent,
  ContentDimensions,
  ModeOption,
  SeriesOverrides,
  ThresholdOptions,
  UnitOptions,
} from '@/core';
import { useChartsTheme } from '../context/ChartsProvider';
import { PanelTooltipWrapper } from '../PanelTooltip';
import {
  DEFAULT_PANEL_TOOLTIP_CONFIG,
  PANEL_TOOLTIP_CONFIG_PINNING_DISABLED,
  type PanelTooltipConfig,
} from '../PanelTooltip/model';
import type { TopListTooltipData } from './model';
import { TopListTooltipContent } from './TopListTooltipContent';
import { computeXAxisTicks, formatDisplayValue, getBarColor, getThresholdLines, getTooltipData } from './utils';

const BAR_GAP = 6;
const BAR_HEIGHT = 14;
const BAR_INNER_GAP = 2;
const MAX_BAR_HEIGHT = 56;
const MAX_GAP = 20;
const MAX_LABEL_WIDTH_FRACTION = 1 / 3;
const MIN_LABEL_WIDTH = 60;
const LABEL_PADDING = 16; // 8px paddingRight + 8px visual buffer
const X_AXIS_HEIGHT = 24;
const THRESHOLD_TOP_PADDING = 20;
const VIRTUALIZE_THRESHOLD = 50;
const VALUE_LABEL_MIN_WIDTH = 48;
const MIN_TICK_SPACING_PX = 85;
const LABEL_MEASURE_SAMPLE_SIZE = 200;

export interface TopListBaseProps extends ContentDimensions {
  data: BarChartData[] | null;
  maxValue?: number;
  unit?: UnitOptions;
  mode?: ModeOption;
  overrides?: SeriesOverrides;
  thresholds?: ThresholdOptions;
  primaryColor: string;
  isCompareMode?: boolean;
  comparisonPeriodLabel?: string;
  onBarClick?: (e: BarClickEvent) => void;
  tooltipConfig?: PanelTooltipConfig;
  panelKey: string;
}

interface BarRowProps {
  index: number;
  datum: BarChartData;
  xMax: number;
  xMin: number;
  trackWidth: number;
  labelWidth: number;
  rowHeight: number;
  barHeight: number;
  mode: ModeOption;
  unit: UnitOptions;
  isCompareMode?: boolean;
  getBarColorForDatum: (datum: BarChartData) => string;
  getComparisonBarColor: (datum: BarChartData) => string;
  onBarClick?: (e: BarClickEvent) => void;
  onMouseEnter: (index: number) => void;
}

const BarRow = React.memo(function BarRow({
  index,
  datum,
  xMax,
  xMin,
  trackWidth,
  labelWidth,
  rowHeight,
  barHeight,
  mode,
  unit,
  isCompareMode,
  getBarColorForDatum,
  getComparisonBarColor,
  onBarClick,
  onMouseEnter,
}: BarRowProps) {
  // Use pixel values derived from trackWidth so bars align exactly with the
  // axis lines (SplitLinesContainer / XAxisContainer use the same trackWidth).
  const axisRange = xMax - xMin;
  const zeroOffsetPx = axisRange > 0 ? ((0 - xMin) / axisRange) * trackWidth : 0;

  let fillWidthPx = 0;
  let fillOffsetPx = 0;
  if (datum.value !== null && datum.value !== undefined && axisRange > 0) {
    fillWidthPx = Math.min((Math.abs(datum.value) / axisRange) * trackWidth, trackWidth);
    fillOffsetPx = datum.value >= 0 ? zeroOffsetPx : zeroOffsetPx - fillWidthPx;
  }
  const barColor = getBarColorForDatum(datum);

  let comparisonFillWidthPx = 0;
  let comparisonFillOffsetPx = 0;
  if (isCompareMode && datum.comparisonValue !== null && datum.comparisonValue !== undefined && axisRange > 0) {
    comparisonFillWidthPx = Math.min((Math.abs(datum.comparisonValue) / axisRange) * trackWidth, trackWidth);
    comparisonFillOffsetPx = datum.comparisonValue >= 0 ? zeroOffsetPx : zeroOffsetPx - comparisonFillWidthPx;
  }
  const comparisonColor = isCompareMode ? getComparisonBarColor(datum) : 'transparent';

  const displayValue =
    datum.value !== null && datum.value !== undefined ? formatDisplayValue({ value: datum.value, mode, unit }) : '';
  const comparisonDisplayValue =
    isCompareMode && datum.comparisonValue !== null && datum.comparisonValue !== undefined
      ? formatDisplayValue({ value: datum.comparisonValue, mode, unit })
      : '';

  return (
    <BarRowContainer
      direction="row"
      rowHeight={rowHeight}
      onMouseEnter={() => onMouseEnter(index)}
      onClick={(e) =>
        onBarClick?.({ name: datum.label, event: { event: e.nativeEvent }, data: { value: datum.value } })
      }
    >
      <BarLabel variant="caption" labelWidth={labelWidth}>
        {datum.label}
      </BarLabel>
      <BarTrack>
        <BarLine>
          <BarFill style={getBarFillStyle(`${fillWidthPx}px`, `${fillOffsetPx}px`, barColor, barHeight)} />
          <ValueLabel variant="caption">{displayValue}</ValueLabel>
        </BarLine>
        {isCompareMode && (
          <BarLine
            onClick={
              onBarClick
                ? (e) => {
                    e.stopPropagation();
                    onBarClick({
                      name: datum.label,
                      event: { event: e.nativeEvent },
                      data: { value: datum.comparisonValue ?? null },
                    });
                  }
                : undefined
            }
            style={onBarClick ? { cursor: 'pointer' } : undefined}
          >
            <BarFill
              style={getBarFillStyle(
                `${comparisonFillWidthPx}px`,
                `${comparisonFillOffsetPx}px`,
                comparisonColor,
                barHeight,
                true
              )}
            />
            <ValueLabel variant="caption">{comparisonDisplayValue}</ValueLabel>
          </BarLine>
        )}
      </BarTrack>
    </BarRowContainer>
  );
});

/**
 * HTML/React-based bar chart component.
 * Native DOM elements for full interactivity (clickable labels, accessible content, etc.).
 */
export function TopListBase(props: TopListBaseProps) {
  const {
    width,
    height,
    data,
    maxValue,
    unit = { kind: 'Decimal' },
    mode = 'value',
    overrides,
    thresholds,
    primaryColor,
    isCompareMode,
    comparisonPeriodLabel,
    onBarClick,
    // Since onBarClick can be used for custom context menu, ensure pinning is disabled to avoid conflicts.
    tooltipConfig = onBarClick ? PANEL_TOOLTIP_CONFIG_PINNING_DISABLED : DEFAULT_PANEL_TOOLTIP_CONFIG,
    panelKey,
  } = props;

  const chartsTheme = useChartsTheme();
  const { thresholds: thresholdColors } = chartsTheme;

  const [hoveredIndex, setHoveredIndex] = useState<number | undefined>(undefined);

  // Resolve the theme-aware font string for caption text (used by BarLabel).
  const captionFont = useTypographyVariantFontForMeasuringText('caption');

  // Size the label column to the widest label, capped at 1/3 of the panel.
  // For large datasets, sample evenly-spaced labels plus the longest by character
  // count to avoid calling measureText on every item.
  const labelWidth = useMemo(() => {
    const maxFraction = Math.floor(width * MAX_LABEL_WIDTH_FRACTION);
    if (!data?.length) return maxFraction;

    let sampled = data;
    if (data.length > LABEL_MEASURE_SAMPLE_SIZE) {
      const step = Math.ceil(data.length / LABEL_MEASURE_SAMPLE_SIZE);
      const subset: BarChartData[] = [];
      let longestIdx = 0;
      for (let i = 0; i < data.length; i++) {
        if (i % step === 0) subset.push(data[i]!);
        if (data[i]!.label.length > data[longestIdx]!.label.length) longestIdx = i;
      }
      if (longestIdx % step !== 0) subset.push(data[longestIdx]!);
      sampled = subset;
    }

    let maxTextWidth = 0;
    for (const datum of sampled) {
      const metrics = measureText(datum.label, captionFont);
      if (metrics && metrics.width > maxTextWidth) maxTextWidth = metrics.width;
    }
    if (maxTextWidth === 0) {
      const maxChars = sampled.reduce((m, d) => Math.max(m, d.label.length), 0);
      maxTextWidth = maxChars * 7;
    }
    const contentWidth = Math.ceil(maxTextWidth) + LABEL_PADDING;
    return Math.max(MIN_LABEL_WIDTH, Math.min(contentWidth, maxFraction));
  }, [data, width, captionFont]);

  // Height of each bar row
  const rowHeight = isCompareMode ? BAR_HEIGHT * 2 + BAR_INNER_GAP + BAR_GAP : BAR_HEIGHT + BAR_GAP;
  const barHeight = BAR_HEIGHT;

  // Compute effective min/max for scaling bars from data values (not maxValue prop)
  // to match BarChartBase behavior where ECharts auto-scales the axis to data.
  // maxValue is still used separately for threshold calculations.
  const { xMin, xMax } = useMemo(() => {
    if (!data?.length) return { xMin: 0, xMax: 1 };
    let min = 0;
    let max = 0;
    for (const datum of data) {
      if (datum.value !== null && datum.value !== undefined) {
        if (datum.value > max) max = datum.value;
        if (datum.value < min) min = datum.value;
      }
      if (isCompareMode && datum.comparisonValue !== null && datum.comparisonValue !== undefined) {
        if (datum.comparisonValue > max) max = datum.comparisonValue;
        if (datum.comparisonValue < min) min = datum.comparisonValue;
      }
    }
    if (max === 0 && min === 0) return { xMin: 0, xMax: 1 };
    return { xMin: min, xMax: max };
  }, [data, isCompareMode]);

  const getBandDataAtIndex = useMemo(() => {
    if (!data) return undefined;
    return (dataIndex: number) =>
      getTooltipData({
        data,
        dataIndex,
        overrides,
        primaryColor,
        defaultColor: primaryColor,
        thresholds,
        thresholdColors,
        maxValue,
      });
  }, [data, overrides, primaryColor, thresholds, thresholdColors, maxValue]);

  const trackWidth = Math.max(0, width - labelWidth - VALUE_LABEL_MIN_WIDTH);

  const {
    ticks: xAxisTicks,
    axisMin,
    axisMax,
  } = useMemo(() => {
    const desiredTickCount = Math.max(2, Math.floor(trackWidth / MIN_TICK_SPACING_PX));
    const result = computeXAxisTicks({ maxValue: xMax, minValue: xMin, unit, mode, desiredTickCount });

    if (trackWidth <= 0 || result.ticks.length <= 1) return result;

    const axisRange = result.axisMax - result.axisMin;
    if (axisRange <= 0) return result;

    const tickFont = captionFont || '11px sans-serif';
    const minGap = 12;
    const filtered: typeof result.ticks = [];
    let lastRightEdge = -Infinity;

    for (const tick of result.ticks) {
      const leftPx = ((tick.value - result.axisMin) / axisRange) * trackWidth;
      const measured = measureText(tick.label, tickFont);
      const labelW = measured ? measured.width : tick.label.length * 7;
      const leftEdge = leftPx - labelW / 2;

      if (leftEdge >= lastRightEdge + minGap || filtered.length === 0) {
        filtered.push(tick);
        lastRightEdge = leftPx + labelW / 2;
      }
    }

    return { ...result, ticks: filtered };
  }, [xMax, xMin, unit, mode, trackWidth, captionFont]);

  const thresholdLineData = useMemo(() => {
    return getThresholdLines({ thresholds, thresholdColors, maxValue });
  }, [thresholds, thresholdColors, maxValue]);

  const hasThresholdWithLabels = thresholdLineData.filter((t) => t.label).length > 0;

  const getBarColorForDatum = useCallback(
    (datum: BarChartData) => {
      if (datum.value === null) return 'transparent';
      return getBarColor({
        itemStyleParams: {
          name: datum.label,
          color: primaryColor,
          value: datum.value,
        },
        overrides,
        primaryColor,
        thresholds,
        thresholdColors,
        maxValue,
      });
    },
    [overrides, primaryColor, thresholds, thresholdColors, maxValue]
  );

  const getComparisonBarColor = useCallback(
    (datum: BarChartData) => {
      if (datum.comparisonValue === null || datum.comparisonValue === undefined) return 'transparent';
      return getBarColor({
        itemStyleParams: {
          name: datum.label,
          color: primaryColor,
          value: datum.comparisonValue,
        },
        overrides,
        primaryColor,
        thresholds,
        thresholdColors,
        maxValue,
      });
    },
    [overrides, primaryColor, thresholds, thresholdColors, maxValue]
  );

  const enableVirtualization = !!data && data.length > VIRTUALIZE_THRESHOLD;

  const handleMouseEnter = useCallback((index: number) => {
    setHoveredIndex(index);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoveredIndex(undefined);
  }, []);

  // Live tooltip content based on hovered index (updates as mouse moves)
  const liveTooltipData: TopListTooltipData | undefined = useMemo(() => {
    if (hoveredIndex === undefined || !getBandDataAtIndex) return undefined;
    return getBandDataAtIndex(hoveredIndex);
  }, [hoveredIndex, getBandDataAtIndex]);

  // Dynamic bar sizing: scale up row/bar height and gap to fill available
  // vertical space, matching BarChartBase (ECharts) proportions.
  const { effectiveRowHeight, effectiveBarHeight } = useMemo(() => {
    const topPadding = hasThresholdWithLabels ? THRESHOLD_TOP_PADDING : 5;
    const barsAvailableHeight = height - X_AXIS_HEIGHT - topPadding;
    const totalDefaultHeight = (data?.length ?? 0) * rowHeight;

    if (data && data.length > 0 && totalDefaultHeight < barsAvailableHeight) {
      const idealRowHeight = Math.floor(barsAvailableHeight / data.length);
      if (isCompareMode) {
        // ~85% of row for bars, rest for gap
        const bar = Math.min(MAX_BAR_HEIGHT, Math.floor(((idealRowHeight - BAR_INNER_GAP) * 0.85) / 2));
        const gap = Math.max(BAR_GAP, Math.min(idealRowHeight - bar * 2 - BAR_INNER_GAP, MAX_GAP));
        return { effectiveRowHeight: bar * 2 + BAR_INNER_GAP + gap, effectiveBarHeight: bar };
      }
      // ~85% of row for the bar, rest for gap between rows
      const bar = Math.min(MAX_BAR_HEIGHT, Math.floor(idealRowHeight * 0.85));
      const gap = Math.max(BAR_GAP, Math.min(idealRowHeight - bar, MAX_GAP));
      return { effectiveRowHeight: bar + gap, effectiveBarHeight: bar };
    }

    return { effectiveRowHeight: rowHeight, effectiveBarHeight: barHeight };
  }, [data, height, rowHeight, barHeight, hasThresholdWithLabels, isCompareMode]);

  // No data state
  if (data === null || data === undefined || !data.length) {
    return (
      <NoDataContainer sx={{ width, height }}>
        <Typography variant="body2" color="text.secondary">
          No data
        </Typography>
      </NoDataContainer>
    );
  }

  const axisRange = axisMax - axisMin;

  return (
    <PanelTooltipWrapper
      width={width}
      tooltipConfig={tooltipConfig}
      panelKey={panelKey}
      renderTooltip={() => {
        if (!liveTooltipData) return null;
        return (
          <TopListTooltipContent
            data={liveTooltipData}
            unit={unit}
            mode={mode}
            isCompareMode={isCompareMode}
            comparisonPeriodLabel={comparisonPeriodLabel}
          />
        );
      }}
    >
      <ChartContainer style={{ width, height }} onMouseLeave={handleMouseLeave}>
        {/* Y-axis line and vertical split lines aligned with x-axis ticks */}
        <SplitLinesContainer labelWidth={labelWidth}>
          {xAxisTicks.map((tick) => {
            if (tick.value === 0) return null;
            const leftPercent = axisRange > 0 ? ((tick.value - axisMin) / axisRange) * 100 : 0;
            return <SplitLine key={`split-${tick.value}`} style={{ left: `${leftPercent}%` }} />;
          })}
          {/* Y-axis line anchored at zero */}
          {(() => {
            const zeroPercent = axisRange > 0 ? ((0 - axisMin) / axisRange) * 100 : 0;
            return <YAxisLine style={{ left: `${zeroPercent}%` }} />;
          })()}
        </SplitLinesContainer>
        {thresholdLineData.map((line, index) => {
          const ratio = axisRange > 0 ? (line.value - axisMin) / axisRange : 0;
          if (ratio > 1 || ratio < 0) return null;
          const leftPx = labelWidth + ratio * trackWidth;
          return (
            <ThresholdLine
              key={`threshold-line-${index}-${line.value}-${line.color}`}
              lineColor={line.color}
              style={{ left: `${leftPx}px` }}
            >
              {line.label && (
                <ThresholdLabel bgColor={line.labelBackgroundColor} textColor={line.labelColor}>
                  {line.label}
                </ThresholdLabel>
              )}
            </ThresholdLine>
          );
        })}
        <BarsArea
          style={{
            paddingTop: hasThresholdWithLabels ? `${THRESHOLD_TOP_PADDING}px` : '5px',
          }}
        >
          {enableVirtualization ? (
            <Virtuoso
              data={data}
              itemContent={(index, datum) => (
                <BarRow
                  key={datum.label}
                  index={index}
                  datum={datum}
                  xMax={axisMax}
                  xMin={axisMin}
                  trackWidth={trackWidth}
                  labelWidth={labelWidth}
                  rowHeight={effectiveRowHeight}
                  barHeight={effectiveBarHeight}
                  mode={mode}
                  unit={unit}
                  isCompareMode={isCompareMode}
                  getBarColorForDatum={getBarColorForDatum}
                  getComparisonBarColor={getComparisonBarColor}
                  onBarClick={onBarClick}
                  onMouseEnter={handleMouseEnter}
                />
              )}
              style={{ height: '100%' }}
              fixedItemHeight={effectiveRowHeight}
            />
          ) : (
            data.map((datum, index) => (
              <BarRow
                key={`bar-row-${index}-${datum.label}`}
                index={index}
                datum={datum}
                xMax={axisMax}
                xMin={axisMin}
                trackWidth={trackWidth}
                labelWidth={labelWidth}
                rowHeight={effectiveRowHeight}
                barHeight={effectiveBarHeight}
                mode={mode}
                unit={unit}
                isCompareMode={isCompareMode}
                getBarColorForDatum={getBarColorForDatum}
                getComparisonBarColor={getComparisonBarColor}
                onBarClick={onBarClick}
                onMouseEnter={handleMouseEnter}
              />
            ))
          )}
        </BarsArea>
        <XAxisContainer labelWidth={labelWidth}>
          {xAxisTicks.map((tick, index) => {
            const leftPercent = axisRange > 0 ? ((tick.value - axisMin) / axisRange) * 100 : 0;
            return (
              <XAxisTick key={`x-axis-tick-${index}-${tick.value}`} style={{ left: `${leftPercent}%` }}>
                {tick.label}
              </XAxisTick>
            );
          })}
        </XAxisContainer>
      </ChartContainer>
    </PanelTooltipWrapper>
  );
}

const ChartContainer = styled('div')({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const BarsArea = styled('div')({
  position: 'relative',
  flex: 1,
  overflowY: 'auto',
  minHeight: 0,
});

const BarRowContainer = styled(Stack, {
  shouldForwardProp: (prop) => prop !== 'rowHeight',
})<{ rowHeight: number }>(({ theme, rowHeight }) => ({
  alignItems: 'center',
  height: `${rowHeight}px`,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const BarLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'labelWidth',
})<{ labelWidth: number }>(({ labelWidth }) => ({
  width: `${labelWidth}px`,
  minWidth: `${labelWidth}px`,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  textAlign: 'right',
  paddingRight: '8px',
  flexShrink: 0,
  userSelect: 'text',
}));

const BarTrack = styled('div')({
  flex: 1,
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: `${BAR_INNER_GAP}px`,
  height: '100%',
});

const BarLine = styled('div')({
  display: 'flex',
  alignItems: 'center',
});

const BarFill = styled('div')({
  borderRadius: '1px',
  transition: 'width 200ms ease, margin-left 200ms ease',
});

function getBarFillStyle(
  fillWidth: string,
  fillOffset: string,
  fillColor: string,
  barHeight: number,
  striped?: boolean
): React.CSSProperties {
  return {
    height: `${barHeight}px`,
    width: fillWidth,
    marginLeft: fillOffset,
    backgroundColor: striped ? 'transparent' : fillColor,
    ...(striped && {
      backgroundImage: `repeating-linear-gradient(-45deg, ${fillColor}, ${fillColor} 3px, transparent 3px, transparent 6px)`,
    }),
  };
}

const ValueLabel = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
  whiteSpace: 'nowrap',
  flexShrink: 0,
  minWidth: '40px',
}));

const XAxisContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'labelWidth',
})<{ labelWidth: number }>(({ labelWidth }) => ({
  position: 'relative',
  height: `${X_AXIS_HEIGHT}px`,
  flexShrink: 0,
  marginLeft: `${labelWidth}px`,
  marginRight: `${VALUE_LABEL_MIN_WIDTH}px`,
}));

const XAxisTick = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: '4px',
  transform: 'translateX(-50%)',
  whiteSpace: 'nowrap',
  color: theme.palette.text.primary,
  fontSize: '11px',
}));

const SplitLinesContainer = styled('div', {
  shouldForwardProp: (prop) => prop !== 'labelWidth',
})<{ labelWidth: number }>(({ labelWidth }) => ({
  position: 'absolute',
  top: 0,
  bottom: `${X_AXIS_HEIGHT}px`,
  left: `${labelWidth}px`,
  right: `${VALUE_LABEL_MIN_WIDTH}px`,
  pointerEvents: 'none',
}));

const YAxisLine = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  bottom: 0,
  width: '1px',
  backgroundColor: theme.featureFlags.panwRebrandStubYear ? theme.palette.border.bold : theme.palette.text.primary,
}));

const SplitLine = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  bottom: 0,
  width: '1px',
  backgroundColor: theme.featureFlags.panwRebrandStubYear ? theme.palette.border.default : theme.palette.divider,
}));

const ThresholdLine = styled('div', {
  shouldForwardProp: (prop) => prop !== 'lineColor',
})<{ lineColor: string }>(({ lineColor }) => ({
  position: 'absolute',
  top: 0,
  bottom: `${X_AXIS_HEIGHT}px`,
  width: '1px',
  borderLeft: `2px dashed ${lineColor}`,
  pointerEvents: 'none',
  zIndex: 1,
}));

const ThresholdLabel = styled('span')<{ bgColor?: string; textColor?: string }>(({ bgColor, textColor }) => ({
  position: 'absolute',
  top: '2px',
  left: '50%',
  transform: 'translateX(-50%)',
  fontSize: '11px',
  padding: '1px 4px',
  whiteSpace: 'nowrap',
  color: textColor,
  backgroundColor: bgColor,
  borderRadius: '2px',
}));

const NoDataContainer = styled(Stack)({
  alignItems: 'center',
  justifyContent: 'center',
});
